import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import './download-page.css';

const REMOTE_CLIENT_DOWNLOAD_URL = '/releases/hosty-remote-client.exe?download_source=web';

type IconName = 'download' | 'package' | 'refresh' | 'shield' | 'support';

function DownloadGlyph({ name, size = 24 }: { name: IconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  if (name === 'download') {
    return <svg {...common}><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>;
  }
  if (name === 'package') {
    return <svg {...common}><path d="m21 8-9-5-9 5 9 5 9-5Z" /><path d="m3 8 9 5 9-5" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></svg>;
  }
  if (name === 'refresh') {
    return <svg {...common}><path d="M20 7v5h-5" /><path d="M4 17v-5h5" /><path d="M6.1 8a7 7 0 0 1 11.5-2L20 8" /><path d="m4 16 2.4 2a7 7 0 0 0 11.5-2" /></svg>;
  }
  if (name === 'shield') {
    return <svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></svg>;
  }
  return <svg {...common}><path d="M4 14a8 8 0 0 1 16 0" /><path d="M18 19c1.1 0 2-.9 2-2v-3h-3v5h1Z" /><path d="M6 19c-1.1 0-2-.9-2-2v-3h3v5H6Z" /><path d="M15 21h-3" /></svg>;
}

const benefits: Array<{ icon: IconName; title: string; text: string }> = [
  { icon: 'package', title: 'Todo en uno', text: 'Una sola instalación' },
  { icon: 'refresh', title: 'Siempre al día', text: 'Se actualiza automáticamente' },
  { icon: 'shield', title: 'Actualiza con tranquilidad', text: 'Tus datos se conservan' },
];

const activationSteps = [
  { number: '1', title: 'Descarga', text: 'El instalador' },
  { number: '2', title: 'Instala', text: 'En tu TPV' },
  { number: '3', title: 'Activa', text: 'Tu licencia' },
];

interface UpdateInfo {
  version: string;
  notes?: string;
  highlights?: string;
  downloadUrl?: string;
  filename?: string;
}

async function fetchChannelInfo(channel: string): Promise<UpdateInfo | null> {
  const response = await fetch(`/api/updates/info?channel=${channel}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error('No se pudo consultar la descarga. Vuelve a intentarlo más tarde o contacta con Hosty.');
  return (await response.json()) as UpdateInfo;
}

export function DownloadPage() {
  const [stableInfo, setStableInfo] = useState<UpdateInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadReleases() {
      try {
        const stable = await fetchChannelInfo('stable');
        if (cancelled) return;
        setStableInfo(stable);
        setError(stable ? null : 'Todavía no hay instalador publicado.');
      } catch {
        if (!cancelled) setError('No se pudo consultar la descarga. Vuelve a intentarlo más tarde o contacta con Hosty.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadReleases();
    return () => { cancelled = true; };
  }, []);

  const info = stableInfo;

  return (
    <section className="section download-page">
      <div className="container download-shell">
        <header className="download-hero">
          <span className="download-eyebrow">TPV para Windows</span>
          <h1>Descarga Hosty TPV</h1>
          <p>Instala, activa tu licencia y empieza a trabajar.</p>
        </header>

        <article className="download-primary-card">
          <div className="download-primary-card__visual" aria-hidden="true">
            <DownloadGlyph name="download" size={46} />
          </div>
          <div className="download-primary-card__content">
            <div className="download-primary-card__heading">
              <div>
                <span className="download-status">Versión estable</span>
                <h2>Hosty TPV para Windows</h2>
              </div>
              {info ? <span className="download-version">v{info.version}</span> : null}
            </div>

            <ul className="download-meta" aria-label="Requisitos y características">
              <li>Windows 10/11</li><li>64 bits</li><li>Actualizaciones automáticas</li>
            </ul>

            <div className="download-actions">
              {loading ? (
                <Button loading size="lg">Comprobando versión…</Button>
              ) : info?.downloadUrl ? (
                <a href={info.downloadUrl} download={info.filename ?? true} target="_self" rel="noopener">
                  <Button type="button" size="lg"><DownloadGlyph name="download" size={20} />Descargar para Windows</Button>
                </a>
              ) : (
                <Button disabled size="lg">Descargar para Windows</Button>
              )}
              <a className="download-manual-link" href="/docs/TPV-Hosty-Manual-de-usuario.pdf?download_source=web" target="_blank" rel="noopener noreferrer">Ver manual PDF</a>
            </div>

            {error ? <p className="download-error" role="status">{error}</p> : null}
            {info && (info.highlights || info.notes) ? (
              <details className="download-release-details">
                <summary>Novedades de la versión {info.version}</summary>
                <p>{info.highlights || info.notes}</p>
              </details>
            ) : null}
          </div>
        </article>

        <ul className="download-benefits">
          {benefits.map((benefit) => (
            <li key={benefit.title}>
              <span className="download-benefits__icon"><DownloadGlyph name={benefit.icon} /></span>
              <span><strong>{benefit.title}</strong><small>{benefit.text}</small></span>
            </li>
          ))}
        </ul>

        <section className="download-activation" aria-labelledby="activation-title">
          <div className="download-activation__heading">
            <div><span className="download-section-label">En tres pasos</span><h2 id="activation-title">Listo para empezar</h2></div>
            <Link to="/panel/licencias"><Button variant="secondary">Gestionar licencias</Button></Link>
          </div>
          <ol className="download-activation__steps">
            {activationSteps.map((step) => (
              <li key={step.number}><span>{step.number}</span><div><strong>{step.title}</strong><small>{step.text}</small></div></li>
            ))}
          </ol>
        </section>

        <aside className="download-support">
          <span className="download-support__icon" aria-hidden="true"><DownloadGlyph name="support" size={30} /></span>
          <div className="download-support__copy"><h2>¿Necesitas ayuda?</h2><p>Descarga Hosty Remote y te guiamos.</p></div>
          <a href={REMOTE_CLIENT_DOWNLOAD_URL} download rel="noopener"><Button type="button">Descargar Hosty Remote</Button></a>
        </aside>
      </div>
    </section>
  );
}
